import {
  CashRoundingIncrement,
  CashRoundingStrategy,
  Money as PBMoney,
} from "gen/proto/money/models_pb"

import { abs, numberWithCommas } from "./helpers"
import { ensure } from "../../utils/nullish/nullish"
import { Decimal } from "../decimal"

export enum Format {
  DEFAULT = "-$9,999.99",
  ACCOUNTING = "($9,999.99)",
  NO_DOLLAR_SIGN = "-9,999.99",
  NO_CENTS = "-$10,000",
  ABSOLUTE = "$9,999.99",
  DEFAULT_WITH_POSITIVE_SIGN = "+$9,999.99",
}

export { CashRoundingIncrement, CashRoundingStrategy }

// Money is the class that should represent all instances of monetary values
// in our frontend code. There are a few reasons to do this:
//
// 1) Avoid common pitfalls from representing money with floating-point
//    numbers (such as rounding errors).
// 2) Collect all money logic in one place to improve maintainability.
// 3) Ensure a consistent convention is used when dealing with currencies.
//
// A Money can be created from numbers originally in units of cents or
// dollars. From then on, the Money object can be treated as a black box,
// and the various methods can be used to operate on them. We should strive to
// convert all incoming money values (e.g. from user input, backend
// responses) immediately and keep this representation until the value is
// finally either displayed to the user with format() or sent out of the
// frontend.
export class Money implements PBMoney {
  cents: bigint

  private constructor(cents: bigint) {
    this.cents = cents
  }

  // zero returns a zero-valued Money.
  static zero(): Money {
    return new Money(0n)
  }

  // fromCents constructs a Money from a number in units of cents.
  static fromCents(cents: bigint) {
    return new Money(cents)
  }

  // fromDollars constructs a Money from a number or string in units of dollars.
  static fromDollars(dollars: number | string) {
    return new Money(
      BigInt(
        Decimal.fromBigSource(dollars)
          .mult(Decimal.fromBigSource(100))
          .toFixed(0)
      )
    )
  }

  // fromPB constructs a Money from our protobuf Money message defined
  // in rd/proto/money/models.proto. If the input is undefined, fromPB will
  // return zero.
  static fromPB(pbMoney?: PBMoney) {
    if (!pbMoney) {
      return Money.zero()
    }
    return new Money(pbMoney.cents)
  }

  static fromPBOrThrow(pbMoney?: PBMoney, message = "Missing Money"): Money {
    return Money.fromPB(ensure(pbMoney, message))
  }

  // sum is a helper function to return a sum of Moneys.
  static sum(...elems: Money[]): Money {
    return elems.reduce((prev, curr) => prev.add(curr), new Money(0n))
  }

  // eqFromPB is a helper function to check if two possibly undefined PBMoneys are equal
  static eqFromPB(moneyA?: PBMoney, moneyB?: PBMoney): boolean {
    if (!moneyA && !moneyB) {
      return true
    }

    if (!!moneyA && !!moneyB) {
      return Money.fromPB(moneyA).eq(Money.fromPB(moneyB))
    }

    return false
  }

  format(f?: Format): string {
    const isNegative = this.cents < 0
    let prefix = isNegative ? "-$" : "$"
    let suffix = ""
    let decimalPlaces = 2
    switch (f) {
      case Format.ACCOUNTING:
        prefix = isNegative ? "($" : "$"
        suffix = isNegative ? ")" : ""
        break
      case Format.NO_DOLLAR_SIGN:
        prefix = isNegative ? "-" : ""
        break
      case Format.NO_CENTS:
        decimalPlaces = 0
        break
      case Format.ABSOLUTE:
        prefix = "$"
        break
      case Format.DEFAULT_WITH_POSITIVE_SIGN:
        prefix = isNegative ? "-$" : "+$"
        break
    }

    return `${prefix}${numberWithCommas(
      Decimal.fromBigSource(abs(this.cents).toString())
        .div(Decimal.fromBigSource(100))
        .toFixed(decimalPlaces)
    )}${suffix}`
  }

  formatAccounting(): string {
    const amountString = `$${numberWithCommas(
      Decimal.fromBigSource(abs(this.cents).toString())
        .div(Decimal.fromBigSource(100))
        .toFixed(2)
    )}`

    if (this.cents < 0) {
      return `(${amountString})`
    }

    return amountString
  }

  // add returns a new Money representing the sum of this and other. The
  // original values are unmodified.
  add(other?: Money): Money {
    return new Money(this.cents + (other?.cents ?? 0n))
  }

  // sub returns a new Money representing the difference of this and
  // other. The original values are unmodified.
  sub(other?: Money): Money {
    return new Money(this.cents - (other?.cents ?? 0n))
  }

  // mult multiplies by the given Decimal. The original values are unmodified.
  mult(d: Decimal): Money {
    return new Money(
      BigInt(
        d.mult(Decimal.fromBigSource(this.cents.toString())).round(0).toString()
      )
    )
  }

  // div returns this divided by other. The returned value is unitless.
  div(other: Money): number {
    return Number(this.cents) / Number(other.cents)
  }

  // divNumber returns this divided by other. The returned value is a Money type.
  divNumber(other: number): Money {
    return new Money(
      BigInt(
        Decimal.fromBigSource(this.cents.toString())
          .div(Decimal.fromBigSource(other))
          .round(0)
          .toString()
      )
    )
  }

  // divDecimal returns this divided by other. The returned value is a Money type.
  divDecimal(other: Decimal): Money {
    return new Money(
      BigInt(
        Decimal.fromBigSource(this.cents.toString())
          .div(other)
          .round(0)
          .toString()
      )
    )
  }

  // cmp compares the two Moneys. The return value is:
  // >  0 if this > other
  // == 0 if this == other
  // <  0 if this < other
  // This function is intended for use in sorting; to compare
  // two currencies, use lt, le, ge, or gt.
  cmp(other: Money): number {
    if (this.cents === other.cents) {
      return 0
    }
    if (this.cents > other.cents) {
      return 1
    }
    return -1
  }

  // lt returns whether a money is strictly less than another money.
  lt(other: Money): boolean {
    return this.cents < other.cents
  }

  // lte returns whether a money is less than or equal to another money.
  lte(other: Money): boolean {
    return this.cents <= other.cents
  }

  // gt returns whether a money is strictly greater than another money.
  gt(other: Money): boolean {
    return this.cents > other.cents
  }

  // gte returns whether a money is greater than or equal to another money.
  gte(other: Money): boolean {
    return this.cents >= other.cents
  }

  // eq returns whether a Money is equal to another Money.
  eq(other: Money): boolean {
    return this.cents === other.cents
  }

  // min returns the smaller of this Money and another Money.
  min(other: Money): Money {
    return this.cents < other.cents ? this : other
  }

  // max returns the larger of this Money and another Money.
  max(other: Money): Money {
    return this.cents > other.cents ? this : other
  }

  // isZero returns whether this Money is equal to zero.
  isZero(): boolean {
    return this.cents === 0n
  }

  // isNonZero returns whether this Money is not equal to zero.
  isNonZero(): boolean {
    return !this.isZero()
  }

  // roundUpToNext99 rounds up this Money to the next highest $0.99
  // applies to positive Money value
  roundUpToNext99(): Money {
    if (this.cents < 0) {
      return this
    }

    const dollars = Math.floor(Number(this.cents) / 100)
    const roundedUpCents = dollars * 100 + 99
    return new Money(BigInt(roundedUpCents))
  }

  // roundUpToNext09 rounds up this Money to the next highest value ending in .09, .19, .29, etc.
  // within the same dollar amount. Examples: 1.07 -> 1.09, 1.16 -> 1.19, 4.23 -> 4.29
  // applies to positive Money value
  roundUpToNext09(): Money {
    if (this.cents < 0) {
      return this
    }

    const dollars = Math.floor(Number(this.cents) / 100)
    const cents = Number(this.cents) % 100
    const roundedUpCents =
      dollars * 100 + Math.floor((cents + 10) / 10) * 10 - 1
    return new Money(BigInt(roundedUpCents))
  }

  // roundUpToNext05 rounds up this Money to the next highest multiple of 5 cents (.00, .05, .10, .15, .20, etc.)
  // within the same dollar amount. Examples: 1.03 -> 1.05, 1.16 -> 1.20, 4.32 -> 4.35, 4.37 -> 4.40
  // applies to positive Money value
  roundUpToNext05(): Money {
    if (this.cents < 0) {
      return this
    }

    const dollars = Math.floor(Number(this.cents) / 100)
    const cents = Number(this.cents) % 100
    const roundedUpCents = dollars * 100 + Math.floor((cents + 4) / 5) * 5
    return new Money(BigInt(roundedUpCents))
  }

  // roundToCashIncrement rounds this Money to a multiple of increment for
  // cash tender.
  roundToCashIncrement(
    increment: CashRoundingIncrement,
    strategy: CashRoundingStrategy
  ): Money {
    const incrementCents = increment === CashRoundingIncrement.NICKEL ? 5n : 0n
    if (incrementCents <= 0n || strategy === CashRoundingStrategy.UNDEFINED) {
      return this
    }
    const cents = this.cents
    const r = cents % incrementCents
    if (r === 0n) {
      return this
    }

    let result: bigint
    switch (strategy) {
      case CashRoundingStrategy.NEAREST: {
        const absCents = abs(cents)
        const absR = absCents % incrementCents
        if (2n * absR < incrementCents) {
          result = absCents - absR
        } else {
          result = absCents + (incrementCents - absR)
        }
        if (cents < 0n) {
          result = -result
        }
        if (cents !== 0n && result === 0n) {
          result = cents > 0n ? incrementCents : -incrementCents
        }
        break
      }
      case CashRoundingStrategy.ROUND_UP:
        result = cents > 0n ? cents - r + incrementCents : cents - r
        break
      case CashRoundingStrategy.ROUND_DOWN:
        result = cents > 0n ? cents - r : cents - r - incrementCents
        break
      default:
        return this
    }
    return new Money(result)
  }

  // absolute returns the absolute value in the Money class
  absolute(): Money {
    return new Money(abs(this.cents))
  }

  // negative returns the negative value in the Money class
  negative(): Money {
    return new Money(-this.cents)
  }

  isPositive(): boolean {
    return this.cents > 0n
  }

  isNegative(): boolean {
    return this.cents < 0n
  }
}
